// Inline SVG icon set (F37 §12 redesign). The prototype used Lucide; we
// inline the handful of glyphs the redesign needs rather than add a runtime
// dependency, keeping the single tenant-neutral bundle small. Each icon
// inherits `currentColor` so a Tailwind `text-*` class on the parent (or the
// element itself) drives the stroke — no per-icon colour prop, no raw hex.
//
// Stroke 1.75px, 24×24 viewBox, round caps/joins — the DS "navy Lucide"
// substitute set. Decorative by default (aria-hidden); pass a `title` for
// the rare standalone-meaning case.

interface IconProps {
  /** Pixel size (width === height). */
  size?: number;
  /** Extra classes — typically a `text-*` colour utility. */
  className?: string;
  /** When set, the icon is exposed to AT with this accessible name;
   * otherwise it is decorative (aria-hidden). */
  title?: string;
}

export type IconName =
  | "shield-check"
  | "clock"
  | "alert-triangle"
  | "check-circle"
  | "arrow-right"
  | "info"
  | "search"
  | "chevron-right"
  | "file-text"
  | "building"
  | "refresh";

// Path/children per glyph. `currentColor` stroke; no fill.
const PATHS: Record<IconName, JSX.Element> = {
  "shield-check": (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  "alert-triangle": (
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  "arrow-right": (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  "chevron-right": <path d="m9 18 6-6-6-6" />,
  "file-text": (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </>
  ),
  building: (
    <>
      <rect width="16" height="20" x="4" y="2" rx="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
    </>
  ),
  refresh: (
    <>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 21v-5h5" />
    </>
  ),
};

/** Render one inline SVG glyph. Decorative unless `title` is given. */
export function Icon({
  name,
  size = 16,
  className,
  title,
}: IconProps & { name: IconName }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
